import { fetchMyAccess } from 'api/roles'
import constants from 'app/constants'
import { isUserAuthenticated, showToast } from 'app/helpers'

import * as types from '../types/access'
import * as authTypes from '../types/auth'

/**
 * Load the current user's Alfred access once, at the top of the authenticated
 * shell. Everything that gates (sidebar, routes, tab strips) reads the result
 * from redux rather than re-fetching.
 *
 * MUST NOT run without a valid token. MainLayout is the shell for every
 * main-layout route and mounts BEFORE PrivateRoute can redirect a logged-out
 * user, so this thunk does get invoked in a logged-out state. If it called
 * /base/me anyway, the 401 would hit the global response interceptor in
 * request.js, which treats ANY 401 as a dead session: it dispatches LOGOUT_START
 * -> clearStorage -> redirectToOrigin() -> "/" -> redirect to a main-layout path
 * -> MainLayout mounts -> /base/me -> 401 -> ... an infinite refresh loop that
 * makes the login page unusable. (It did exactly that on dev, 2026-07-13.)
 *
 * Deliberately dispatches NOTHING when unauthenticated: marking access as
 * "loaded" here would stick the session with a fail-open (unrestricted) value,
 * and MainLayout's `if (!loaded)` guard would then never re-fetch after the user
 * actually logs in — handing them the whole sidebar regardless of their role.
 * Leaving it unloaded is correct: MainLayout mounts again post-login and asks.
 */
export const loadMyAccess = () => async (dispatch) => {
  if (!isUserAuthenticated()) return

  dispatch({ type: types.FETCH_ACCESS })
  try {
    const access = await fetchMyAccess()

    // Access switched off. The backend already refuses this user's Alfred calls
    // (AlfredAccessEnabled -> 403), so there is nothing useful left to render.
    // /base/me is deliberately left reachable for them precisely so we can say
    // WHY, instead of leaving them on a page of silently failing requests.
    if (access && access.enabled === false) {
      showToast(
        'Your access to Alfred has been disabled. Please contact an admin.',
        constants.ERROR
      )
      dispatch({ type: authTypes.LOGOUT_START })
      return
    }

    dispatch({ type: types.FETCH_ACCESS_SUCCESS, access })
  } catch (error) {
    // Swallowed on purpose — see the fail-open note in reducers/access.js.
    dispatch({ type: types.FETCH_ACCESS_FAILED })
  }
}
